import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  ColorPicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../lib/api';
import type { App, AppPrice } from '../types';
import { normalizeHexColor } from '../components/apps/AppCard';

const { Title, Text } = Typography;

type PriceFormValues = {
  stripePriceId: string;
  interval: 'MONTH' | 'YEAR' | 'CUSTOM';
  name?: string;
  currency?: string;
  unitAmount?: number | null;
  isActive?: boolean;
  metadata?: string;
};

type AppFormValues = {
  name: string;
  slug: string;
  description: string;
  link: string;
  badge?: string | null;
  sortOrder?: number;
  stripeProductId?: string | null;
  isActive?: boolean;
  isAnneaOnly?: boolean;
  isFree?: boolean;
  accessMode?: 'FREE' | 'SUBSCRIPTION' | 'ADMIN_GRANTED' | 'CATALOG_GROUP';
  catalogVisibility?: 'PUBLIC' | 'ACCESS_ONLY';
  parentId?: string | null;
  iconColor?: string | null;
  infoUrl?: string | null;
};

function SubAppRow({
  subApp,
  onEdit,
  onDelete,
  onUpdated,
  onAddPrice,
}: {
  subApp: App;
  onEdit: (app: App) => void;
  onDelete: (appId: string) => void;
  onUpdated: () => void;
  onAddPrice?: (app: App) => void;
}) {
  const [localBadge, setLocalBadge] = useState(subApp.badge ?? '');
  const [localSortOrder, setLocalSortOrder] = useState(subApp.sortOrder ?? 0);
  const [isActive, setIsActive] = useState(subApp.isActive);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalBadge(subApp.badge ?? '');
    setLocalSortOrder(subApp.sortOrder ?? 0);
    setIsActive(subApp.isActive);
  }, [subApp.badge, subApp.sortOrder, subApp.isActive]);

  const saveField = async (field: Parameters<typeof api.admin.updateApp>[1]) => {
    try {
      setSaving(true);
      await api.admin.updateApp(subApp.id, field);
      onUpdated();
    } catch (error: any) {
      message.error(error.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 12px',
        background: '#fafafa',
        borderRadius: 6,
        border: '1px solid #f0f0f0',
      }}
    >
      <div style={{ minWidth: 180 }}>
        <Text strong style={{ fontSize: 13 }}>
          {subApp.name}
        </Text>
        {subApp.isAdminGranted && <Tag color="orange">Admin-granted</Tag>}
        {subApp.catalogVisibility === 'ACCESS_ONLY' && <Tag>Visible with access</Tag>}
        {subApp.isFree && (
          <Tag color="cyan" style={{ marginLeft: 6, fontSize: 11 }}>
            Free
          </Tag>
        )}
        {subApp.isAnneaOnly && (
          <Tag color="gold" style={{ marginLeft: 6, fontSize: 11 }}>
            Annea only
          </Tag>
        )}
        <br />
        <Text type="secondary" style={{ fontSize: 11 }}>
          {subApp.slug}
        </Text>
      </div>

      <div style={{ flex: 1 }} />

      <Space size={12} align="center">
        <Space size={4} align="center">
          <Text type="secondary" style={{ fontSize: 12 }}>Badge</Text>
          <Input
            size="small"
            value={localBadge}
            onChange={(e) => setLocalBadge(e.target.value)}
            onBlur={() => {
              const trimmed = localBadge.trim();
              if (trimmed !== (subApp.badge ?? '')) {
                saveField({ badge: trimmed || null });
              }
            }}
            placeholder="BETA"
            style={{ width: 80 }}
          />
        </Space>

        <Space size={4} align="center">
          <Text type="secondary" style={{ fontSize: 12 }}>Sort</Text>
          <InputNumber
            size="small"
            value={localSortOrder}
            onChange={(val) => setLocalSortOrder(val ?? 0)}
            onBlur={() => {
              if (localSortOrder !== (subApp.sortOrder ?? 0)) {
                saveField({ sortOrder: localSortOrder });
              }
            }}
            style={{ width: 64 }}
          />
        </Space>

        <Switch
          size="small"
          checked={isActive}
          loading={saving}
          onChange={(checked) => {
            setIsActive(checked);
            saveField({ isActive: checked });
          }}
          checkedChildren="On"
          unCheckedChildren="Off"
        />

        {onAddPrice && <Button size="small" onClick={() => onAddPrice(subApp)}>Add Price</Button>}
        <Button size="small" onClick={() => onEdit(subApp)}>Edit</Button>
        <Popconfirm
          title="Delete app?"
          description="All prices will also be deleted."
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => onDelete(subApp.id)}
        >
          <Button size="small" danger>Delete</Button>
        </Popconfirm>
      </Space>
    </div>
  );
}

export default function AdminAppsPage() {
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [appModalOpen, setAppModalOpen] = useState(false);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [editingPrice, setEditingPrice] = useState<AppPrice | null>(null);
  const [priceApp, setPriceApp] = useState<App | null>(null);
  const [saving, setSaving] = useState(false);
  const [appForm] = Form.useForm<AppFormValues>();
  const [priceForm] = Form.useForm<PriceFormValues>();
  const watchedIconColor = Form.useWatch('iconColor', appForm);
  const watchedAccessMode = Form.useWatch('accessMode', appForm);
  const watchedParentId = Form.useWatch('parentId', appForm);
  const selectedParent = apps.find((app) => app.id === watchedParentId);
  const isCatalogGroup = watchedAccessMode === 'CATALOG_GROUP';

  useEffect(() => {
    loadApps();
  }, []);

  const loadApps = async () => {
    try {
      setLoading(true);
      const data = await api.admin.listApps();
      setApps(data.apps || []);
    } catch (error: any) {
      message.error(error.message || 'Failed to load apps');
    } finally {
      setLoading(false);
    }
  };

  const openCreateApp = () => {
    setEditingApp(null);
    appForm.resetFields();
    appForm.setFieldsValue({ isActive: true, isAnneaOnly: false, sortOrder: 0, isFree: false, accessMode: 'SUBSCRIPTION', catalogVisibility: 'PUBLIC' });
    setAppModalOpen(true);
  };

  const openEditApp = (app: App) => {
    const parent = apps.find((candidate) => candidate.id === app.parentId);
    const adminGranted = app.isAdminGranted || (parent?.isAdminGranted && !parent.isCatalogGroup);
    setEditingApp(app);
    appForm.setFieldsValue({
      name: app.name,
      slug: app.slug,
      description: app.description,
      link: app.link,
      badge: app.badge || undefined,
      sortOrder: app.sortOrder || 0,
      isActive: app.isActive,
      isAnneaOnly: app.isAnneaOnly || false,
      stripeProductId: app.stripeProductId || undefined,
      isFree: app.isFree || false,
      accessMode: app.isCatalogGroup ? 'CATALOG_GROUP' : adminGranted ? 'ADMIN_GRANTED' : app.isFree ? 'FREE' : 'SUBSCRIPTION',
      catalogVisibility: app.catalogVisibility || 'PUBLIC',
      parentId: app.parentId || undefined,
      iconColor: app.iconColor || undefined,
      infoUrl: app.infoUrl || undefined,
    });
    setAppModalOpen(true);
  };

  const handleSaveApp = async () => {
    try {
      const values = await appForm.validateFields();
      const normalizedIconColor = normalizeHexColor(values.iconColor);
      if (values.iconColor && !normalizedIconColor) {
        message.error('Icon color must be a valid hex color, for example #9bd2ae');
        return;
      }
      const { accessMode, ...appValues } = values;
      const payload = {
        ...appValues,
        isFree: accessMode === 'FREE',
        isAdminGranted: (!values.parentId || apps.find((app) => app.id === values.parentId)?.isCatalogGroup) && accessMode === 'ADMIN_GRANTED',
        isCatalogGroup: accessMode === 'CATALOG_GROUP',
        parentId: values.parentId || null,
        stripeProductId: accessMode === 'CATALOG_GROUP' ? null : values.stripeProductId || null,
        link: accessMode === 'CATALOG_GROUP' ? '' : values.link,
        catalogVisibility: accessMode === 'CATALOG_GROUP' ? 'PUBLIC' as const : values.catalogVisibility,
        iconColor: normalizedIconColor,
      };
      setSaving(true);
      if (editingApp) {
        await api.admin.updateApp(editingApp.id, payload);
        message.success('App updated');
      } else {
        await api.admin.createApp(payload);
        message.success('App created');
      }
      setAppModalOpen(false);
      await loadApps();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error.message || 'Failed to save app');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApp = async (appId: string) => {
    try {
      await api.admin.deleteApp(appId);
      message.success('App deleted');
      await loadApps();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete app');
    }
  };

  const openCreatePrice = (app: App) => {
    setEditingPrice(null);
    setPriceApp(app);
    priceForm.resetFields();
    priceForm.setFieldsValue({ interval: 'MONTH', currency: 'EUR', isActive: true });
    setPriceModalOpen(true);
  };

  const openEditPrice = (app: App, price: AppPrice) => {
    setEditingPrice(price);
    setPriceApp(app);
    priceForm.setFieldsValue({
      stripePriceId: price.stripePriceId,
      interval: price.interval,
      name: price.name || undefined,
      currency: price.currency,
      unitAmount: price.unitAmount,
      isActive: price.isActive,
      metadata: price.metadata ? JSON.stringify(price.metadata, null, 2) : undefined,
    });
    setPriceModalOpen(true);
  };

  const parseMetadata = (raw?: string) => {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    return JSON.parse(trimmed) as Record<string, unknown>;
  };

  const handleSavePrice = async () => {
    try {
      if (!priceApp) return;
      const values = await priceForm.validateFields();
      let metadata: Record<string, unknown> | null = null;
      try {
        metadata = parseMetadata(values.metadata);
      } catch {
        message.error('Metadata must be valid JSON');
        return;
      }

      const payload = {
        stripePriceId: values.stripePriceId,
        interval: values.interval,
        name: values.name || null,
        currency: values.currency || 'usd',
        unitAmount:
          typeof values.unitAmount === 'number' ? values.unitAmount : null,
        isActive: typeof values.isActive === 'boolean' ? values.isActive : true,
        metadata,
      };

      setSaving(true);
      if (editingPrice) {
        await api.admin.updatePrice(editingPrice.id, payload);
        message.success('Price updated');
      } else {
        await api.admin.createPrice(priceApp.id, payload);
        message.success('Price created');
      }
      setPriceModalOpen(false);
      await loadApps();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error.message || 'Failed to save price');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePrice = async (priceId: string) => {
    try {
      await api.admin.deletePrice(priceId);
      message.success('Price deleted');
      await loadApps();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete price');
    }
  };

  const getPriceColumns = (app: App): ColumnsType<AppPrice> => [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (value) => value || '—' },
    { title: 'Interval', dataIndex: 'interval', key: 'interval' },
    { title: 'Currency', dataIndex: 'currency', key: 'currency' },
    {
      title: 'Unit Amount',
      dataIndex: 'unitAmount',
      key: 'unitAmount',
      render: (value) => (typeof value === 'number' ? value : '—'),
    },
    { title: 'Stripe Price ID', dataIndex: 'stripePriceId', key: 'stripePriceId' },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value) => (
        <Tag color={value ? 'green' : 'default'}>{value ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, price) => (
        <Space>
          <Button size="small" onClick={() => openEditPrice(app, price)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete price?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeletePrice(price.id)}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderAppTitle = (app: App) => (
    <Space>
      <Text>{app.name}</Text>
      <Tag color={app.isActive ? 'green' : 'default'}>{app.isActive ? 'Active' : 'Inactive'}</Tag>
      {app.isAnneaOnly && <Tag color="gold">Annea only</Tag>}
      {app.isCatalogGroup && <Tag>Catalog group</Tag>}
      {!app.isCatalogGroup && app.isFree && <Tag color="cyan">Free</Tag>}
      {app.isAdminGranted && <Tag color="orange">Admin-granted</Tag>}
      {app.catalogVisibility === 'ACCESS_ONLY' && <Tag>Visible with access</Tag>}
      {app.badge && <Tag color="blue">{app.badge}</Tag>}
      {app.parentId && <Tag color="purple">Sub-app</Tag>}
    </Space>
  );

  const renderAppActions = (app: App) => (
    <Space>
      {!app.isCatalogGroup && (!app.parentId || app.parent?.isCatalogGroup) && <Button onClick={() => openCreatePrice(app)}>Add Price</Button>}
      <Button onClick={() => openEditApp(app)}>Edit</Button>
      <Popconfirm
        title="Delete app?"
        description="All prices will also be deleted."
        okText="Delete"
        okButtonProps={{ danger: true }}
        onConfirm={() => handleDeleteApp(app.id)}
      >
        <Button danger>Delete</Button>
      </Popconfirm>
    </Space>
  );

  const renderAppCardContent = (app: App) => (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Text type="secondary">Slug: {app.slug}</Text>
      {!app.isCatalogGroup && <Text type="secondary">Link: {app.link}</Text>}
      {app.parentId && (
        <Text type="secondary">
          Parent: {apps.find((candidate) => candidate.id === app.parentId)?.name || app.parentId}
        </Text>
      )}
      {!app.isCatalogGroup && <Text type="secondary">Stripe Product: {app.stripeProductId || '-'}</Text>}
    </Space>
  );

  const sortedParentApps = apps
    .filter((app) => !app.parentId)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Admin: Apps & Prices</Title>
          <Text type="secondary">Manage suites (apps) and their Stripe price links</Text>
        </div>
        <Button type="primary" onClick={openCreateApp}>
          New App
        </Button>
      </div>

      {sortedParentApps.map((app) => {
        const subApps = apps
          .filter((child) => child.parentId === app.id)
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

        return (
          <Card key={app.id} title={renderAppTitle(app)} extra={renderAppActions(app)}>
            {renderAppCardContent(app)}

            {!app.isCatalogGroup && (
              <div style={{ marginTop: 16 }}>
                <Table
                  dataSource={app.prices || []}
                  columns={getPriceColumns(app)}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  loading={loading}
                />
              </div>
            )}

            {subApps.length > 0 && (
              <>
                <Divider orientation="left">
                  <Text type="secondary" style={{ fontSize: 13 }}>{app.isCatalogGroup ? 'Apps in this group' : 'Sub-apps'}</Text>
                </Divider>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {subApps.map((subApp) => (
                    <div key={subApp.id}>
                      <SubAppRow
                        subApp={subApp}
                        onEdit={openEditApp}
                        onDelete={handleDeleteApp}
                        onUpdated={loadApps}
                        onAddPrice={app.isCatalogGroup ? openCreatePrice : undefined}
                      />
                      {app.isCatalogGroup && Boolean(subApp.prices?.length) && (
                        <Table
                          dataSource={subApp.prices || []}
                          columns={getPriceColumns(subApp)}
                          rowKey="id"
                          size="small"
                          pagination={false}
                        />
                      )}
                    </div>
                  ))}
                </Space>
              </>
            )}
          </Card>
        );
      })}

      <Modal
        title={editingApp ? 'Edit App' : 'Create App'}
        open={appModalOpen}
        onOk={handleSaveApp}
        onCancel={() => setAppModalOpen(false)}
        okText={editingApp ? 'Save' : 'Create'}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={appForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="accessMode"
            label="App type / access"
            extra={isCatalogGroup
              ? 'Organizes apps without a group license. Appears when at least one contained app is visible.'
              : selectedParent?.isCatalogGroup
              ? 'This app has its own licensing and visibility.'
              : watchedParentId ? 'Admin-granted access is inherited from the parent app.' : undefined}
          >
            <Select disabled={Boolean(selectedParent?.isAdminGranted && !selectedParent.isCatalogGroup)} options={[
              { value: 'FREE', label: 'Free' },
              { value: 'SUBSCRIPTION', label: 'Subscription with automatic trial' },
              { value: 'ADMIN_GRANTED', label: 'Admin-granted', disabled: Boolean(watchedParentId && !selectedParent?.isCatalogGroup) },
              { value: 'CATALOG_GROUP', label: 'Catalog group', disabled: Boolean(watchedParentId) },
            ]} />
          </Form.Item>
          <Form.Item name="parentId" label="Parent app or catalog group (optional)" hidden={isCatalogGroup}>
            <Select
              onChange={(parentId) => {
                const parent = apps.find((candidate) => candidate.id === parentId);
                if (parent?.isAdminGranted && !parent.isCatalogGroup) appForm.setFieldValue('accessMode', 'ADMIN_GRANTED');
                else if (parentId && !parent?.isCatalogGroup && ['ADMIN_GRANTED', 'CATALOG_GROUP'].includes(appForm.getFieldValue('accessMode'))) appForm.setFieldValue('accessMode', 'SUBSCRIPTION');
              }}
              placeholder="No parent (standalone app)"
              allowClear
              options={apps
                .filter((candidate) => !candidate.parentId)
                .map((candidate) => ({
                  value: candidate.id,
                  label: candidate.isCatalogGroup ? `${candidate.name} (Catalog group)` : candidate.name,
                  disabled: editingApp?.id === candidate.id,
                }))}
            />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="App name" />
          </Form.Item>
          <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
            <Input placeholder="app-slug" />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: !isCatalogGroup }]}>
            <Input.TextArea rows={3} placeholder="Short description" />
          </Form.Item>
          <Form.Item name="link" label="Link" hidden={isCatalogGroup} rules={[{ required: !isCatalogGroup }]}>
            <Input placeholder="https://example.com/app" />
          </Form.Item>
          <Form.Item name="infoUrl" label="Info URL" help="Optional link to additional info or documentation">
            <Input placeholder="https://example.com/docs" />
          </Form.Item>
          <Form.Item name="badge" label="Badge">
            <Input placeholder="BETA / NEW" />
          </Form.Item>
          <Form.Item name="iconColor" label="Icon Color" help="Leave empty for auto-assigned color. Sub-apps inherit parent color.">
            <Space>
              <ColorPicker
                value={normalizeHexColor(watchedIconColor) || undefined}
                onChange={(color) => {
                  appForm.setFieldsValue({
                    iconColor: normalizeHexColor(color.toHexString()),
                  });
                }}
                allowClear
                onClear={() => appForm.setFieldsValue({ iconColor: null })}
              />
              <Input
                placeholder="#1e3a8a"
                value={watchedIconColor || ''}
                onChange={(e) => appForm.setFieldsValue({ iconColor: e.target.value || null })}
                onBlur={(e) => {
                  const normalized = normalizeHexColor(e.target.value);
                  if (normalized) {
                    appForm.setFieldsValue({ iconColor: normalized });
                  }
                }}
                style={{ width: 120 }}
              />
            </Space>
          </Form.Item>
          <Form.Item name="stripeProductId" label="Stripe Product ID" hidden={isCatalogGroup}>
            <Input
              placeholder="prod_xxx"
              disabled={Boolean(watchedParentId && !selectedParent?.isCatalogGroup)}
            />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sort Order">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="catalogVisibility" label="Catalog visibility" hidden={isCatalogGroup} extra="Users need current access to see a restricted app. Parent visibility also applies to sub-apps.">
            <Select options={[
              { value: 'PUBLIC', label: 'Everyone' },
              { value: 'ACCESS_ONLY', label: 'Only users with access' },
            ]} />
          </Form.Item>
          <Form.Item name="isAnneaOnly" label="Visible only to Annea org users" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingPrice ? 'Edit Price' : 'Create Price'}
        open={priceModalOpen}
        onOk={handleSavePrice}
        onCancel={() => setPriceModalOpen(false)}
        okText={editingPrice ? 'Save' : 'Create'}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={priceForm} layout="vertical" requiredMark={false}>
          <Form.Item name="stripePriceId" label="Stripe Price ID" rules={[{ required: true }]}>
            <Input placeholder="price_xxx" />
          </Form.Item>
          <Form.Item name="interval" label="Interval" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'MONTH', label: 'Monthly' },
                { value: 'YEAR', label: 'Yearly' },
                { value: 'CUSTOM', label: 'Custom' },
              ]}
            />
          </Form.Item>
          <Form.Item name="name" label="Name">
            <Input placeholder="Monthly / Annual / Enterprise" />
          </Form.Item>
          <Form.Item name="currency" label="Currency">
            <Input placeholder="usd" />
          </Form.Item>
          <Form.Item name="unitAmount" label="Unit Amount (cents)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="metadata" label="Metadata (JSON)">
            <Input.TextArea rows={4} placeholder='{"tier":"standard"}' />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
